import React, { useState } from 'react';
import { Modal, Chip, Fold, checkboxClasses, inputClasses, primaryButton, secondaryButton } from '../components/ui';
import { ignoreCandidates, ownerLabel } from '../lib/files';
import { MAX_NAMED, plural } from '../lib/text';

const REASON_MAX = 500; // el mismo tope que el servidor

// Por qué se ignora: siempre se ofrece, nunca se exige. Queda a la vista en la fila de Ignorados.
// `shared` avisa que ese motivo va a quedar en más de una marca de ignorado.
export const ReasonField = ({ value, onChange, shared }) => (
    <section>
        <label htmlFor="ignore-reason" className="block mb-1">Motivo <span className="text-slate-500">(opcional)</span></label>
        <textarea id="ignore-reason" rows={2} maxLength={REASON_MAX} value={value} onChange={e => onChange(e.target.value)}
            placeholder="Por qué se ignora…" className={`${inputClasses} w-full resize-y`} />
        {shared && <p className="mt-1 text-xs text-slate-500">El mismo motivo queda para todos.</p>}
    </section>
);

// "Vas a ignorar ESTOS updates para estos clientes": nada se ignora sin ver antes qué y para quién.
export default function IgnoreDialog({ files, fileList, clients, selectedClients, version, onConfirm, onCancel }) {
    const { candidates, preselected, notApplicable } = ignoreCandidates(files, fileList, clients, selectedClients);
    const [chosen, setChosen] = useState(preselected);
    const selectable = candidates.filter(c => c.status === 'available').map(c => c.client);
    const toggle = (client) => setChosen(prev => prev.includes(client) ? prev.filter(c => c !== client) : [...prev, client]);
    const total = candidates.filter(c => chosen.includes(c.client)).reduce((sum, c) => sum + c.available, 0);
    const single = fileList.length === 1;
    const [reason, setReason] = useState('');

    return (
        <Modal title={single ? 'Ignorar update' : 'Ignorar updates'} onClose={onCancel} footer={<>
            <button type="button" onClick={onCancel} className={secondaryButton}>Cancelar</button>
            <button type="button" disabled={chosen.length === 0} onClick={() => onConfirm(selectable.filter(c => chosen.includes(c)), reason.trim())} className={primaryButton}>
                {chosen.length === 0 ? 'Elegí al menos un cliente' : `Ignorar para ${plural(chosen.length, 'cliente', 'clientes')}`}
            </button>
        </>}>
            <section>
                <p className="mb-2">Vas a ignorar <strong>{single ? 'un solo update' : plural(fileList.length, 'update', 'updates')}</strong> en la versión <span className="font-mono">{version}</span>:</p>
                <ul aria-label="Updates a ignorar" className="border border-zafiro-200 bg-zafiro-50 rounded-lg divide-y divide-zafiro-100 max-h-44 overflow-y-auto">
                    {fileList.map(file => (
                        <li key={file.name} className="flex items-center justify-between gap-2 px-3 py-1.5">
                            <span className="font-mono text-[13px] font-medium truncate" title={file.name}>{file.name}</span>
                            <Chip tone={file.universal ? 'blue' : 'neutral'}>{ownerLabel(file)}</Chip>
                        </li>
                    ))}
                </ul>
                <p className="mt-2 text-xs text-slate-500">No se le publica al cliente hasta recuperarlo desde Ignorados.</p>
            </section>

            <section>
                <div className="flex items-center justify-between mb-2">
                    <p>Para <strong>{plural(chosen.length, 'cliente', 'clientes')}</strong>{selectedClients.length === 0 && <span className="text-slate-500"> (no elegiste ninguno arriba: se proponen todos)</span>}:</p>
                    {selectable.length > 1 && <button type="button" onClick={() => setChosen(chosen.length === selectable.length ? [] : selectable)} className="text-xs font-semibold text-zafiro-600 hover:underline">{chosen.length === selectable.length ? 'Deseleccionar todos' : 'Seleccionar todos'}</button>}
                </div>
                {candidates.length === 0
                    ? <p className="p-3 rounded-lg bg-slate-50 text-slate-500">No hay clientes a los que les corresponda.</p>
                    : <ul aria-label="Clientes a ignorar" className="grid grid-cols-2 gap-1 max-h-56 overflow-y-auto p-1 border border-slate-200 rounded-lg">
                        {candidates.map(({ client, status, available }) => (
                            <li key={client}>
                                <label className={`flex items-center gap-2 px-2 py-1.5 rounded-md ${status === 'available' ? 'cursor-pointer hover:bg-slate-50' : 'text-slate-400'} ${chosen.includes(client) ? 'bg-zafiro-50' : ''}`}>
                                    <input type="checkbox" aria-label={client} className={checkboxClasses} checked={chosen.includes(client)} disabled={status !== 'available'} onChange={() => toggle(client)} />
                                    <span className="flex-grow font-mono text-[13px] font-medium">{client}</span>
                                    {status === 'ignored' && <Chip>ya ignorado</Chip>}
                                    {status === 'published' && <Chip tone="amber" title="Lo publicado se ignora desde su fila de Publicados">publicado</Chip>}
                                    {status === 'available' && !single && available < fileList.length && <Chip title="Los demás no le corresponden, o ya los tiene publicados o ignorados">{available} de {fileList.length}</Chip>}
                                </label>
                            </li>
                        ))}
                    </ul>}
                {notApplicable.length > 0 && (
                    <p className="mt-2 text-xs text-slate-500">
                        {notApplicable.length > MAX_NAMED
                            ? <>No les corresponde a {notApplicable.length} clientes elegidos <Fold about="a quién no le corresponde"><span className="font-mono">{notApplicable.join(', ')}</span></Fold></>
                            : `No ${notApplicable.length === 1 ? 'le' : 'les'} corresponde: ${notApplicable.join(', ')}.`}
                    </p>
                )}
                {candidates.some(c => c.status === 'published') && <p className="mt-2 text-xs text-slate-500">Lo publicado se ignora desde Publicados.</p>}
            </section>

            <ReasonField value={reason} onChange={setReason} shared={total > 1} />

            <p className="p-3 rounded-lg bg-slate-50"><strong>Total: {plural(total, 'marca de ignorado', 'marcas de ignorado')}.</strong></p>
        </Modal>
    );
}

// Ignorar un publicado: un update, un cliente. Deja de entregársele, sin despublicarlo antes.
export function IgnorePublishedDialog({ name, client, version, onConfirm, onCancel }) {
    const [reason, setReason] = useState('');
    return (
        <Modal title="Ignorar update publicado" onClose={onCancel} footer={<>
            <button type="button" onClick={onCancel} className={secondaryButton}>Cancelar</button>
            <button type="button" onClick={() => onConfirm(reason.trim())} className={primaryButton}>Ignorar para {client}</button>
        </>}>
            <p>Se va a ignorar <strong>sólo este update</strong>, <span className="font-mono text-[13px] font-medium break-all">{name}</span>, para <strong className="font-mono">{client}</strong> en <span className="font-mono">{version}</span>: deja de entregársele y pasa a Ignorados.</p>
            <ReasonField value={reason} onChange={setReason} />
        </Modal>
    );
}
